import { Link, useLocation } from "react-router-dom";
import { useNavLabels } from "@/hooks/useNavLabels";
import { useState, useEffect } from "react";

export default function Header() {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [scrolled, setScrolled] = useState(false);
  const { items: menuItems } = useNavLabels();

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setScrolled(currentScrollY > 10);

      if (currentScrollY > lastScrollY && currentScrollY > 80) {
        setHidden(true);
      } else {
        setHidden(false);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ease-in-out overflow-hidden ${
          hidden ? "-translate-y-full" : "translate-y-0"
        } ${scrolled ? "bg-black/40 backdrop-blur-md" : ""}`}
      >
        <div className="w-full px-4 md:px-10 lg:px-16">
          <div className="flex items-center justify-between h-16 md:h-24">
            {/* Logo */}
            <Link to="/" className="flex items-center flex-shrink-0">
              <img
                src="https://storage.readdy-site.link/project_files/dd803486-334c-44db-be94-f45bc3cd2f43/d8037b81-fa3c-48ec-97fe-f681366e9749_MMT-APAIZADO-BLANCO.png?v=a0e907dcb1ef384db5cf53182a5a04c1"
                alt="MásMed Tarapacá"
                className="h-10 md:h-16 w-auto object-contain"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {menuItems.map((item) => {
                const active = isActive(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={
                      active
                        ? "px-5 py-2.5 bg-white text-primary rounded-full text-base whitespace-nowrap transition-all duration-200 cursor-pointer hover:bg-white hover:text-gray-600"
                        : "px-5 py-2.5 text-white/90 hover:bg-white hover:text-gray-600 rounded-full text-base whitespace-nowrap transition-all duration-200 cursor-pointer"
                    }
                  >
                    {item.label}
                  </Link>
                );
              })}
            </nav>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center text-white cursor-pointer flex-shrink-0"
              aria-label="Toggle menu"
            >
              <i className={`ri-${mobileMenuOpen ? "close-line" : "menu-3-line"} text-2xl`}></i>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu panel — OUTSIDE header so header doesn't grow */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed top-16 left-0 right-0 bg-white/95 backdrop-blur-sm shadow-lg z-50">
          <nav className="flex flex-col py-3 px-4">
            {menuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`px-4 py-3 rounded-lg text-lg transition-colors duration-200 cursor-pointer ${
                  isActive(item.path)
                    ? "bg-primary/10 text-primary"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </>
  );
}